import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { parseGroupScheduleData } from '@/utils/groupScheduleParser';

export interface GroupParticipant {
  id: string;
  letter: string; // A, B, C
  player1: string;
  player2?: string;
  isUser: boolean;
}

export interface GroupMatch {
  id: string;
  matchId: string; // G-Masculino 1era-A-AB
  participants: [string, string]; // ["A", "B"]
  time: string; // "18:00"
  date: string; // "2025-10-24"
  court?: string;
}

export interface GroupInfo {
  groupName: string; // "Grupo A"
  category: string; // "Masculino 1era"
  subcategory?: string; // "A", "B"
  date: string; // "2025-10-24"
  startTime: string; // "18:00"
  endTime: string; // "21:00"
  court?: string; // "1"
  participants: GroupParticipant[];
  matches: GroupMatch[];
}

export function useUserGroupInfo(eventId: string) {
  const { user } = useAuth();
  const [groupInfo, setGroupInfo] = useState<GroupInfo | null>(null);
  const [allGroupInfos, setAllGroupInfos] = useState<GroupInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const processRegistration = async (
    userRegistration: any,
    userId: string
  ): Promise<GroupInfo | null> => {
    if (!userRegistration || !userRegistration.group) {
      return null;
    }

    const userGroup = (userRegistration.group?.trim() || '').replace(/\s+/g, ' ');
    const userCategory = (userRegistration.category?.trim() || '').replace(/\s+/g, ' ');
    const userSubcategory = userRegistration.subcategory?.trim() || null;

    const fullCategory = userSubcategory 
      ? `${userCategory} ${userSubcategory}`
      : userCategory;

    console.log('[useUserGroupInfo] Processing registration:', {
      group: userGroup,
      category: fullCategory
    });

    // Get all participants in the same group + category + subcategory
    let groupRegQuery = supabase
      .from('registrations')
      .select(`id, user_id, partner_user_id, player1_name, player2_name`)
      .eq('event_id', eventId)
      .eq('group', userGroup)
      .eq('status', 'confirmed');
    
    if (userCategory) {
      groupRegQuery = groupRegQuery.eq('category', userCategory);
    }
    if (userSubcategory) {
      groupRegQuery = groupRegQuery.eq('subcategory', userSubcategory);
    } else {
      groupRegQuery = groupRegQuery.is('subcategory', null);
    }
    
    const { data: groupRegistrations, error: groupError } = await groupRegQuery
      .order('registered_at', { ascending: true });

    if (groupError) throw groupError;

    // Get group calendar data
    let { data: groupCalendars, error: calendarError } = await supabase
      .from('group_calendars' as any)
      .select('*')
      .eq('tournament_id', eventId)
      .eq('group_name', userGroup)
      .eq('category', fullCategory)
      .order('created_at', { ascending: true });

    if ((!groupCalendars || groupCalendars.length === 0) && !calendarError) {
      const { data: calendarsIlike, error: errorIlike } = await supabase
        .from('group_calendars' as any)
        .select('*')
        .eq('tournament_id', eventId)
        .ilike('group_name', userGroup)
        .ilike('category', fullCategory)
        .order('created_at', { ascending: true });
      
      groupCalendars = calendarsIlike;
      calendarError = errorIlike;
    }

    if (calendarError) throw calendarError;

    const groupCalendar = groupCalendars && groupCalendars.length > 0 ? groupCalendars[0] : null;

    if (!groupCalendar) {
      // No schedule yet, return basic group info
      const participants: GroupParticipant[] = (groupRegistrations || []).map((reg, idx) => ({
        id: reg.id,
        letter: String.fromCharCode(65 + idx),
        player1: reg.player1_name || 'Por asignar',
        player2: reg.player2_name,
        isUser: reg.user_id === userId || reg.partner_user_id === userId
      }));

      return {
        groupName: userGroup,
        category: userCategory || '',
        subcategory: userSubcategory,
        date: '',
        startTime: '',
        endTime: '',
        participants,
        matches: []
      };
    }

    // Parse group calendar data
    const parsedInfo = parseGroupScheduleData(
      groupCalendar as any,
      userId,
      groupRegistrations || []
    );

    parsedInfo.category = userCategory || '';
    parsedInfo.subcategory = userSubcategory ?? undefined;

    return parsedInfo;
  };

  const fetchGroupInfo = async () => {
    if (!eventId || typeof eventId !== 'string' || eventId.length < 10) {
      console.error('[useUserGroupInfo] Invalid eventId:', { eventId });
      setError('Invalid event ID');
      setLoading(false);
      return;
    }

    if (!user || !eventId) {
      setLoading(false);
      return;
    }

    console.log('[useUserGroupInfo] === STARTING FETCH ===', { eventId, userId: user.id });

    try {
      setLoading(true);
      setError(null);

      // Verify user profile
      const { data: userProfile, error: profileError } = await supabase
        .from('profiles')
        .select('user_id, full_name, email')
        .eq('user_id', user.id)
        .maybeSingle();

      if (profileError || !userProfile) {
        console.error('[useUserGroupInfo] Usuario sin perfil válido:', { userId: user.id });
        setError('Perfil de usuario no encontrado.');
        setLoading(false);
        return;
      }

      // 1. Get ALL user registrations (no .maybeSingle()) - by user_id
      let { data: userRegistrations, error: regError } = await supabase
        .from('registrations')
        .select('user_id, partner_user_id, group, category, subcategory, player1_name, player2_name')
        .eq('event_id', eventId)
        .eq('status', 'confirmed')
        .eq('user_id', user.id);

      console.log('[useUserGroupInfo] Query by user_id:', { 
        count: userRegistrations?.length || 0
      });

      // If not found as user_id, try as partner_user_id
      if ((!userRegistrations || userRegistrations.length === 0) && !regError) {
        const { data: partnerRegistrations, error: partnerError } = await supabase
          .from('registrations')
          .select('user_id, partner_user_id, group, category, subcategory, player1_name, player2_name')
          .eq('event_id', eventId)
          .eq('status', 'confirmed')
          .eq('partner_user_id', user.id);
        
        console.log('[useUserGroupInfo] Query by partner_user_id:', { 
          count: partnerRegistrations?.length || 0
        });
        
        if (partnerRegistrations && partnerRegistrations.length > 0) {
          userRegistrations = partnerRegistrations;
        }
        regError = partnerError;
      }

      // Fallback: search by name
      if ((!userRegistrations || userRegistrations.length === 0) && !regError && userProfile.full_name) {
        console.warn('[useUserGroupInfo] Fallback: searching by name:', userProfile.full_name);
        
        const { data: nameRegistrations, error: nameError } = await supabase
          .from('registrations')
          .select('user_id, partner_user_id, group, category, subcategory, player1_name, player2_name')
          .eq('event_id', eventId)
          .eq('status', 'confirmed')
          .or(`player1_name.ilike.%${userProfile.full_name}%,player2_name.ilike.%${userProfile.full_name}%`);

        if (nameRegistrations && nameRegistrations.length > 0) {
          userRegistrations = nameRegistrations;
        }
        regError = nameError;
      }

      if (regError) throw regError;
      
      if (!userRegistrations || userRegistrations.length === 0) {
        setGroupInfo(null);
        setAllGroupInfos([]);
        setLoading(false);
        return;
      }

      console.log('[useUserGroupInfo] Total registrations found:', userRegistrations.length);

      // Process ALL registrations into GroupInfo[]
      const infos: GroupInfo[] = [];
      for (const reg of userRegistrations) {
        try {
          const info = await processRegistration(reg, user.id);
          if (info) {
            infos.push(info);
          }
        } catch (err) {
          console.error('[useUserGroupInfo] Error processing registration:', err);
        }
      }

      console.log('[useUserGroupInfo] ✅ Processed group infos:', infos.length);
      
      setAllGroupInfos(infos);
      setGroupInfo(infos[0] || null);
    } catch (err) {
      console.error('Error fetching group info:', err);
      setError(err instanceof Error ? err.message : 'Error al cargar información del grupo');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGroupInfo();
  }, [eventId, user?.id]);

  return {
    groupInfo,
    allGroupInfos,
    loading,
    error,
    refetch: fetchGroupInfo
  };
}
